import React, { useState, useRef, useEffect, useCallback } from "react";

// Define the shape of each option object
export interface DropdownOption {
    label: string;
    value: string | number;
}

interface SelectDropdownProps {
    label?: string;
    name: string;
    value: string | number; // The selected value
    onChange: (value: string | number) => void; // Pass the selected value
    options: DropdownOption[];
    error?: string;
    isSearchable?: boolean; // Optional prop to disable search
    isWhiteBg?: boolean;
}

const SelectDropdown: React.FC<SelectDropdownProps> = ({
    label,
    name,
    value,
    onChange,
    options,
    error,
    isSearchable = true,
    isWhiteBg,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [hoveredIndex, setHoveredIndex] = useState(0);
    const [wasOpen, setWasOpen] = useState(false);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const optionRefs = useRef<(HTMLLIElement | null)[]>([]);
    const listRef = useRef<HTMLUListElement | null>(null); // Add this

    // Filter options based on search term
    const filteredOptions = options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Reset state when dropdown is closed
    useEffect(() => {
        if (!isOpen) {
            setSearchTerm("");
            setHoveredIndex(0);
        }
    }, [isOpen]);

    // Focus input when dropdown opens in searchable mode
    useEffect(() => {
        if (isOpen && isSearchable && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen, isSearchable]);

    // Scroll selected option to the TOP when dropdown opens
    useEffect(() => {
        if (isOpen && !wasOpen && filteredOptions.length > 0) {
            const selectedIndex = filteredOptions.findIndex(
                option => option.value === value
            );
            const optionEl = optionRefs.current[selectedIndex];
            const listEl = listRef.current;
            if (
                selectedIndex !== -1 &&
                optionEl &&
                listEl
            ) {
                // Scroll so the selected option is at the top
                listEl.scrollTop = optionEl.offsetTop;
                setHoveredIndex(selectedIndex);
            }
            setWasOpen(true);
        }
        if (!isOpen && wasOpen) {
            setWasOpen(false);
        }
    }, [isOpen, filteredOptions, value, wasOpen]);

    const handleSelect = useCallback((option: DropdownOption) => {
        onChange(option.value);
        setIsOpen(false);
    }, [onChange]);

    // Handle keyboard navigation
    const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
        if (!isOpen) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHoveredIndex(prev => (prev + 1) % filteredOptions.length);
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHoveredIndex(prev => (prev - 1 + filteredOptions.length) % filteredOptions.length);
                break;
            case 'Enter':
            case 'Tab':
                if (filteredOptions.length > 0) {
                    e.preventDefault();
                    handleSelect(filteredOptions[hoveredIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                break;
        }
    }, [isOpen, filteredOptions, hoveredIndex, handleSelect]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const placeholderText = (label || name).toLowerCase();
    const selectedLabel = options.find(option => option.value === value)?.label || `Select a ${placeholderText}`;

    return (
        <div className="relative w-full" ref={dropdownRef} onKeyDown={handleKeyDown}>
            {label && <label className="block text-[#212121] font-medium text-gray-700 mb-2">{label}</label>}
            <div
                className={`w-full pl-3 !pr-12 py-2 form-select border rounded-md cursor-pointer flex justify-between items-center transition-colors duration-200 ${isWhiteBg ? '!bg-white' : 'bg-gray-50'} ${error ? 'border-red-500' : 'border-gray-300'
                    } ${isOpen ? 'border-indigo-500 ring ring-indigo-200 ring-opacity-50' : 'hover:border-gray-400'}`}
                onClick={() => setIsOpen(!isOpen)}
            >
                {isSearchable && isOpen ? (
                    <input
                        ref={inputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setHoveredIndex(0); // Reset hover when typing
                        }}
                        placeholder={`Search in ${placeholderText}...`}
                        className="
                            w-full h-full 
                            bg-transparent 
                            border-none 
                            outline-none 
                            focus:outline-none 
                            focus:ring-0 
                            p-0 
                            m-0
                            text-inherit
                            placeholder:text-gray-400
                        "                    />
                ) : (
                    <span className="text-gray-800 text-[13px] 2xl:text-[16px]">{selectedLabel}</span>
                )}
            </div>
            {isOpen && (
                <ul
                    ref={listRef} // Add this
                    className="p-0 absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                >
                    {filteredOptions.length > 0 ? filteredOptions.map((option, index) => (
                        <li
                            key={option.value}
                            ref={el => optionRefs.current[index] = el}
                            onClick={() => handleSelect(option)}
                            onMouseEnter={() => setHoveredIndex(index)}
                            className={`px-3 py-2 cursor-pointer transition-colors duration-100 ${index === hoveredIndex ? 'bg-indigo-100' : ''
                                } ${value === option.value ? 'text-indigo-800 font-semibold' : ''}`}
                        >
                            {option.label}
                        </li>
                    )) : (
                        <li className="px-3 py-2 text-gray-500">No options found</li>
                    )}
                </ul>
            )}
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
        </div>
    );
};

export default SelectDropdown;